import React, { useState } from 'react';
import { X, Key, Shield, Clock, Wifi } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import { Checkbox } from '../../../components/ui/Checkbox';

export function GenerateKeyModal({ isOpen, onClose, onGenerate }) {
  const [keyName, setKeyName] = useState('');
  const [permissions, setPermissions] = useState(['read']);
  const [usageLimit, setUsageLimit] = useState(10000);
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState(60);
  const [expiresInDays, setExpiresInDays] = useState('');
  const [ipRestriction, setIpRestriction] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!keyName?.trim()) {
      setError('Please enter a key name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const keyData = {
        keyName: keyName?.trim(),
        permissions,
        usageLimit: parseInt(usageLimit) || 10000,
        rateLimitPerMinute: parseInt(rateLimitPerMinute) || 60,
        allowedIpAddresses: ipRestriction ? ipRestriction?.split(',')?.map(ip => ip?.trim()) : null,
        expiresAt: expiresInDays ? new Date(Date.now() + parseInt(expiresInDays) * 24 * 60 * 60 * 1000)?.toISOString() : null
      };

      await onGenerate(keyData);
      handleClose();
    } catch (err) {
      setError(err?.message || 'Failed to generate API key');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setKeyName('');
    setPermissions(['read']);
    setUsageLimit(10000);
    setRateLimitPerMinute(60);
    setExpiresInDays('');
    setIpRestriction('');
    setError('');
    onClose();
  };

  const togglePermission = (permission) => {
    setPermissions(prev => 
      prev?.includes(permission)
        ? prev?.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Generate New API Key</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Key Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Key className="w-4 h-4" />
              Key Name
            </label>
            <Input
              value={keyName}
              onChange={(e) => setKeyName(e?.target?.value)}
              placeholder="e.g., Mobile App - Production"
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              A descriptive name to identify this API key
            </p>
          </div>

          {/* Permissions */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Shield className="w-4 h-4" />
              Permissions
            </label>
            <div className="space-y-2">
              <Checkbox
                label="Read - View artist data"
                checked={permissions?.includes('read')}
                onChange={() => togglePermission('read')}
              />
              <Checkbox
                label="Write - Create and update data"
                checked={permissions?.includes('write')}
                onChange={() => togglePermission('write')}
              />
              <Checkbox
                label="Admin - Full access"
                checked={permissions?.includes('admin')}
                onChange={() => togglePermission('admin')}
              />
            </div>
          </div>

          {/* Usage Limit */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Usage Limit
            </label>
            <Input
              type="number"
              value={usageLimit}
              onChange={(e) => setUsageLimit(e?.target?.value)}
              placeholder="10000"
              min="1"
            />
            <p className="text-sm text-gray-500 mt-1">
              Maximum number of API requests allowed
            </p>
          </div>

          {/* Rate Limit */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Rate Limit (per minute)
            </label>
            <Input
              type="number"
              value={rateLimitPerMinute}
              onChange={(e) => setRateLimitPerMinute(e?.target?.value)}
              placeholder="60"
              min="1"
            />
            <p className="text-sm text-gray-500 mt-1">
              Maximum requests per minute from this key
            </p>
          </div>

          {/* Expiration */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4" />
              Expires In (days)
            </label>
            <Input
              type="number"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e?.target?.value)}
              placeholder="Leave empty for no expiration"
              min="1"
            />
            <p className="text-sm text-gray-500 mt-1">
              Optional: Set an expiration date for this key
            </p>
          </div>

          {/* IP Restriction */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Wifi className="w-4 h-4" />
              IP Address Restrictions
            </label>
            <Input
              value={ipRestriction}
              onChange={(e) => setIpRestriction(e?.target?.value)}
              placeholder="e.g., 192.168.1.1, 10.0.0.1"
            />
            <p className="text-sm text-gray-500 mt-1">
              Optional: Comma-separated list of allowed IP addresses
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Generating...' : 'Generate API Key'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}